// /agents: the Agents pane at full width, where links from Messages and email land
// (?agent=<id>&run=<id>: that agent opens, at that run; a question to ask it, from a form its
// assistant submitted, comes in the navigation's state, never the address, as it holds the
// patient's answers: AssistedFormBadge). On a wide screen the pane shows the list on
// the left and the open agent's chat on the right (AgentsPane's `split`); on a narrow one, either.
// The address follows the agent the person opens (?agent=<id>, or none for the list), so a reload or
// a copied address opens what is on screen; it is replaced, not pushed, so Back leaves the page
// rather than stepping through the agents opened.
//
// Route protection is off app-wide (ProtectedRoute), so the page gates itself as the server does
// (views._allowed): an approved account, which means exactly one of the app's roles (not one waiting
// for approval), whose role roles.json lets into `agentsPage` (the same roles as the Agents tab,
// clinicPage.agentsTab, the server's own gate). The front desk (admin) is one of them, and the
// navbar's Agents link (roles.json agentsPage.navbar) brings every one of them here.
//
// Tailwind is scoped to #clinic-root (tailwind.config.js `important`), which reaches the element's
// descendants only: the page renders its own root, which carries the theme class and nothing else.
// The column is as tall as the window below its top (useFillsWindowBelow), because the pane is
// flex-1 and scrolls inside. The floating bubbles stay hidden here, as they do on the chart, since
// they would cover the chat's composer.
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AgentsPane from './AgentsPane';
import { useAgents } from './useAgents';
import { useUserInfo } from '../../../hooks/medstudent/useUserInfo';
import { canAccessPage, isAllowedRole } from '../../../config/roles';
import { useHideFloatingBubbles } from '../../../utils/floatingBubbles';

// The characters the page uses that aren't ASCII, so this file stays ASCII.
const ROBOT = String.fromCodePoint(0x1f916);
const ELLIPSIS = String.fromCharCode(0x2026);

const TITLE_ID = 'agents-title';
// The longest question a link may bring to fill the opened agent's message box.
const MAX_ASK = 2000;
const NOTE = 'p-4 text-xs text-slate-500 dark:text-slate-400';

// The clinic page's theme: the choice its toggle saved, else the system's.
function prefersDark() {
  try {
    const saved = localStorage.getItem('medstudent-theme');
    if (saved === 'dark' || saved === 'light') return saved === 'dark';
    return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

// The height that fills the window below the element's top. What sits above the page varies (the
// navbar, which grows once the account has loaded, and a view-as session's banner), so the top is
// measured rather than assumed: on mount, when the window is resized, and when the page's own height
// changes. It is the top in the page, not in the window, so a scrolled window measures the same, and
// it is rounded up, so the column is never a pixel too tall.
function useFillsWindowBelow(ref) {
  const [top, setTop] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      setTop(Math.max(0, Math.ceil(ref.current.getBoundingClientRect().top + (window.scrollY || 0))));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(measure) : null;
    if (observer) observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [ref]);
  return top === null ? undefined : `calc(100vh - ${top}px)`;
}

export default function AgentsPage() {
  const userInfo = useUserInfo();
  const { search, state } = useLocation();
  const navigate = useNavigate();
  // The agent a link names, its run and a question to ask it; nothing (the list) when it names no
  // agent.
  const focus = useMemo(() => {
    const params = new URLSearchParams(search);
    const agentId = params.get('agent');
    if (!agentId) return null;
    const ask = typeof state?.ask === 'string' && state.ask.trim() ? state.ask.slice(0, MAX_ASK) : null;
    return { agentId, runId: params.get('run') || null, ask };
  }, [search, state]);
  const followOpen = useCallback((agentId) => {
    navigate({ search: agentId ? `?agent=${encodeURIComponent(agentId)}` : '' }, { replace: true });
  }, [navigate]);
  const [dark] = useState(prefersDark);
  const allowed = isAllowedRole(userInfo.role) && canAccessPage(userInfo.role, 'agentsPage');
  const agentsState = useAgents({ enabled: allowed });
  useHideFloatingBubbles(true);
  const column = useRef(null);
  const height = useFillsWindowBelow(column);

  let body;
  if (userInfo.loading) {
    body = <div role="status" className={NOTE}>{`Loading${ELLIPSIS}`}</div>;
  } else if (!allowed) {
    body = <div className={NOTE}>You don't have access to agents.</div>;
  } else {
    body = (
      <AgentsPane agentsState={agentsState} userId={userInfo.userId} focus={focus} split onOpenChange={followOpen} />
    );
  }

  return (
    <div id="clinic-root" className={dark ? 'dark' : ''}>
      <div className="w-full bg-slate-50 dark:bg-[#0a0a0f] text-slate-900 dark:text-slate-100 antialiased font-sans">
        <div ref={column} style={{ height }}
             className="max-w-6xl mx-auto flex flex-col border-x border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f]">
          <header className="shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-800/60">
            <h1 id={TITLE_ID} className="m-0 text-sm font-bold text-slate-900 dark:text-slate-100">
              <span aria-hidden="true">{ROBOT}</span> Agents
            </h1>
          </header>
          <main aria-labelledby={TITLE_ID} className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            {body}
          </main>
        </div>
      </div>
    </div>
  );
}
